import React from 'react';
import { Box, Divider, Drawer, IconButton, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import { DEEP_GREEN, MUTED } from '../colorTokens';
import { GATE_COLOR, GATE_TITLE, formatDate, formatNumber, formatPercent } from './format';
import { GateChip, KindChip, TestBar } from './ConfidenceParts';
import PlanPrompt from './PlanPrompt';

const ROLE_LABEL = { dev: 'Development', qa: 'Quality assurance', product: 'Product' };
const MODE_LABEL = { manual: 'Manual', automated: 'Automated', mixed: 'Manual and automated', other: 'Other' };
const PRODUCT_LABEL = { legacy: 'Legacy', modern: 'Modern', other: 'Other' };
const TOP_AREAS = 12;

const Section = ({ title, children }) => (
  <Box sx={{ py: 2 }}>
    <Typography sx={{ fontWeight: 600, color: DEEP_GREEN, mb: 1 }}>{title}</Typography>
    {children}
  </Box>
);

const Facts = ({ items }) => (
  <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))', gap: 1.5 }}>
    {items.map(([label, value]) => (
      <Box key={label}>
        <Typography sx={{ fontWeight: 700, color: DEEP_GREEN, fontSize: '1.125rem', lineHeight: 1.2 }}>{value}</Typography>
        <Typography variant="caption" sx={{ color: MUTED }}>{label}</Typography>
      </Box>
    ))}
  </Box>
);

const Empty = ({ children }) => <Typography variant="body2" sx={{ color: MUTED }}>{children}</Typography>;

const Breakdown = ({ rows, labels }) => (
  <Box sx={{ display: 'grid', gap: 1 }}>
    {rows.map((r) => (
      <Box key={r.key} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Typography variant="body2" sx={{ width: 90 }}>{labels[r.key] || r.key}</Typography>
        <TestBar totals={r} width={220} />
      </Box>
    ))}
  </Box>
);

const TestSection = ({ release }) => {
  const { tests } = release;
  if (!tests) return <Empty>No Azure DevOps test plan is named for this release.</Empty>;
  const { totals } = tests;
  return (
    <>
      <TestBar totals={totals} width={360} />
      <Box sx={{ mt: 1.5 }}>
        <Facts
          items={[
            ['Passed', formatNumber(totals.passed)],
            ['Failed', formatNumber(totals.failed)],
            ['Not yet proven', formatNumber(totals.blocked + totals.notExecuted)],
            ['Of what was run, passed', formatPercent(totals.executedPassRate)],
            ['Plan run', formatPercent(totals.coverage, 0)],
            ['Testing window', tests.testingWindow ? `${formatDate(tests.testingWindow.start)} – ${formatDate(tests.testingWindow.end)}` : '–']
          ]}
        />
      </Box>
      <Box sx={{ mt: 2, display: 'grid', gap: 2 }}>
        <Breakdown rows={tests.byProduct.filter((r) => r.key !== 'other')} labels={PRODUCT_LABEL} />
        <Breakdown rows={tests.byMode.filter((r) => r.key !== 'other')} labels={MODE_LABEL} />
      </Box>
      <Typography variant="caption" sx={{ color: MUTED, display: 'block', mt: 1.5 }}>
        Source: {tests.planName}, read {formatDate(tests.syncedAt)}.
      </Typography>
    </>
  );
};

const AreaSection = ({ tests }) => {
  if (!tests || !tests.byArea.length) return null;
  const areas = tests.byArea.slice(0, TOP_AREAS);
  return (
    <>
      <Divider />
      <Section title={`Largest areas (${areas.length} of ${tests.byArea.length})`}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ '& th': { color: MUTED, fontWeight: 500, borderBottomColor: 'rgba(0,0,0,0.08)' } }}>
              <TableCell>Area</TableCell>
              <TableCell align="right">Tests</TableCell>
              <TableCell align="right">Failed</TableCell>
              <TableCell align="right">Passed of run</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {areas.map((a) => (
              <TableRow key={`${a.product}|${a.mode}|${a.area}`}>
                <TableCell>
                  {a.area}
                  <Typography variant="caption" sx={{ color: MUTED, display: 'block', lineHeight: 1.1 }}>{PRODUCT_LABEL[a.product]} · {MODE_LABEL[a.mode]}</Typography>
                </TableCell>
                <TableCell align="right">{formatNumber(a.total)}</TableCell>
                <TableCell align="right" sx={{ color: a.failed ? GATE_COLOR.hold : undefined, fontWeight: a.failed ? 700 : 400 }}>{formatNumber(a.failed)}</TableCell>
                <TableCell align="right">{formatPercent(a.executedPassRate)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Section>
    </>
  );
};

const FailingSection = ({ tests }) => {
  if (!tests || !tests.failing || !tests.failing.length) return null;
  return (
    <>
      <Divider />
      <Section title={`Failing tests (${tests.failing.length}${tests.totals.failed > tests.failing.length ? ` of ${tests.totals.failed}` : ''})`}>
        <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
          {tests.failing.map((f, i) => (
            <Typography component="li" key={`${f.testCaseId}-${i}`} variant="body2" sx={{ mb: 0.5 }}>
              {f.title || `Test case ${f.testCaseId}`}
              <Typography component="span" variant="caption" sx={{ color: MUTED }}> · {f.area}</Typography>
            </Typography>
          ))}
        </Box>
      </Section>
    </>
  );
};

const DECISION_WORD = { ready: 'Go', conditional: 'Conditional Go', hold: 'No-Go' };

const approvalText = (goNoGo) => {
  if (goNoGo.source === 'summary') {
    return `Release summary decision: ${DECISION_WORD[goNoGo.decision]}${goNoGo.decidedAt ? `, summary generated ${formatDate(goNoGo.decidedAt)}` : ''}${goNoGo.summaryFile ? ` (${goNoGo.summaryFile})` : ''}.`;
  }
  if (goNoGo.recorded) {
    return `Final decision: ${DECISION_WORD[goNoGo.decision] || 'Hold'}${goNoGo.decidedAt ? ` on ${formatDate(goNoGo.decidedAt)}` : ''}${goNoGo.decidedBy ? ` by ${goNoGo.decidedBy}` : ''}.`;
  }
  if (goNoGo.summaryNotSet) return 'The release summary shows the Go / No-Go decision as not set.';
  return 'No final Go/No-Go decision is recorded in the portal or in a release summary for this release.';
};

const ApprovalSection = ({ goNoGo }) => (
  <>
    {goNoGo.source !== 'summary' && (
      <Box sx={{ display: 'grid', gap: 0.75 }}>
        {Object.entries(goNoGo.roles).map(([role, r]) => (
          <Box key={role} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {r.approved ? <CheckCircleIcon sx={{ fontSize: 18, color: GATE_COLOR.go }} /> : <RadioButtonUncheckedIcon sx={{ fontSize: 18, color: '#9e9e9e' }} />}
            <Typography variant="body2">{ROLE_LABEL[role]}</Typography>
            <Typography variant="caption" sx={{ color: MUTED }}>{r.approved ? `signed off${r.decidedAt ? ` ${formatDate(r.decidedAt)}` : ''}` : 'no sign-off recorded'}</Typography>
          </Box>
        ))}
      </Box>
    )}
    <Typography variant="body2" sx={{ mt: goNoGo.source === 'summary' ? 0 : 1.5 }}>{approvalText(goNoGo)}</Typography>
  </>
);

const RolloutSection = ({ release }) => {
  const { rollout } = release;
  if (!rollout) return <Empty>{release.stage === 'upcoming' ? 'This release has not started rolling out.' : 'No deployment runs were captured for this release.'}</Empty>;
  return (
    <Facts
      items={[
        ['Instances', formatNumber(rollout.instances)],
        ['Duration', `${rollout.days} d`],
        ['Started', formatDate(rollout.start)],
        ['Failure rate', formatPercent(rollout.failureRate)],
        ['Rollbacks', formatNumber(rollout.rollbacks)],
        ['Recovered', formatNumber(rollout.recovered)],
        ['Still failing', formatNumber(rollout.stillFailing)],
        ['Readiness score', rollout.readinessScore == null ? '–' : `${rollout.readinessScore} / 100`]
      ]}
    />
  );
};

const PlanSection = ({ plan }) => (
  <Facts
    items={[
      ['Deployment weekends', formatNumber(plan.weekends)],
      ['Instances scheduled', formatNumber(plan.instances)],
      ['First weekend', formatDate(plan.dateStart)],
      ['Last weekend', formatDate(plan.dateEnd)]
    ]}
  />
);

const ReleaseDrawer = ({ release, customerView, onClose, onOpenPlans }) => (
  <Drawer anchor="right" open={!!release} onClose={onClose} PaperProps={{ sx: { width: { xs: '100%', sm: 520 }, p: 3 } }}>
    {release && (
      <>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="h5" sx={{ fontWeight: 700, color: DEEP_GREEN }}>{release.version}</Typography>
              <KindChip kind={release.kind} />
            </Box>
            <Typography variant="body2" sx={{ color: MUTED }}>
              {release.stage === 'upcoming' ? 'Upcoming release' : `Released ${formatDate(release.date)}`}
              {customerView ? ' · Customer view' : ''}
            </Typography>
          </Box>
          <IconButton onClick={onClose} aria-label="Close"><CloseIcon /></IconButton>
        </Box>
        <Box sx={{ display: 'flex', gap: 2, mt: 2, mb: 1, flexWrap: 'wrap' }}>
          {['tested', 'approved', 'rolledOut'].map((gate) => (
            <Box key={gate}>
              <Typography variant="caption" sx={{ color: MUTED, display: 'block' }}>{GATE_TITLE[gate]}</Typography>
              <GateChip gate={gate} status={release.gates[gate].status} />
            </Box>
          ))}
        </Box>
        <Divider />
        <Section title="Test evidence"><TestSection release={release} /></Section>
        <AreaSection tests={release.tests} />
        {!customerView && <FailingSection tests={release.tests} />}
        <Divider />
        <Section title="Go / No-Go"><ApprovalSection goNoGo={release.goNoGo} /></Section>
        <Divider />
        <Section title="Rollout"><RolloutSection release={release} /></Section>
        {release.plan && (
          <>
            <Divider />
            <Section title={`Deployment schedule · ${release.plan.name}`}><PlanSection plan={release.plan} /></Section>
          </>
        )}
        {!release.plan && !customerView && (
          <>
            <Divider />
            <Section title="Deployment schedule">
              <PlanPrompt onOpenPlans={onOpenPlans && (() => { onClose(); onOpenPlans(); })}>
                {release.stage === 'upcoming'
                  ? 'No deployment schedule is uploaded for this release. Upload the workbook to see which instances are planned in which wave.'
                  : 'No deployment schedule is uploaded for this release. Upload the workbook to compare what was planned with what was rolled out.'}
              </PlanPrompt>
            </Section>
          </>
        )}
      </>
    )}
  </Drawer>
);

export default ReleaseDrawer;
